// A conversation over a runner: open one with an NPC, read its lines, choose, wait while the game plays a scene the
// script asked for, close. It reports what happens through hooks (the game turns them into its events: a line's
// string id, its words from the strings table and an estimated time on screen, for bubbles, babble and subtitles).
// Plain state, saved with the runner's, so a save in the middle of a talk resumes on the same line.
import { lineSeconds, type DialogueBeat, type DialogueRunner } from "./runner";

/** A line as the game shows it. */
export interface SpokenLine {
  speaker: string;
  stringId: string;
  text: string;
  seconds: number;
}

/** The conversation's state now. */
export interface ConversationState {
  /** Who the player talks with (null: no conversation). */
  npc: string | null;
  script: string | null;
  /** What is showing: a line, the choices, or nothing yet. */
  beat: DialogueBeat | null;
  /** The speaker of the line showing. */
  speaker: string | null;
  /** Scenes the script is waiting on (the box hides till they're done). */
  waiting: string[];
  /** Lines shown in this conversation. */
  lines: number;
}

/** The game's side of a conversation. */
export interface ConversationHooks {
  /** A line's words (the strings table) for its id. */
  text(id: string): string;
  onOpen?(npc: string, script: string): void;
  onLine?(line: SpokenLine): void;
  onChoices?(ids: readonly string[]): void;
  onClose?(npc: string): void;
}

/** Conversations, one at a time. */
export interface Conversations {
  readonly runner: DialogueRunner;
  /** Talk with `npc` through `script` (at `knot`). Throws when one is already open. */
  open(npc: string, script: string, knot?: string): void;
  /** Past the line showing (does nothing while waiting or on choices). */
  advance(): void;
  /** Pick a choice by its place in the list showing (0-based). */
  choose(n: number): void;
  /** Hold the conversation until `release(tag)` (a scene the script asked for). */
  wait(tag: string): void;
  release(tag: string): void;
  /** End it now. */
  close(): void;
  state(): ConversationState;
  /** Whether a conversation is open. */
  active(): boolean;
  snapshot(): unknown;
  restore(data: unknown): void;
}

const idle = (): ConversationState => ({ npc: null, script: null, beat: null, speaker: null, waiting: [], lines: 0 });

/** Build the conversations over a runner. */
export function createConversations(runner: DialogueRunner, hooks: ConversationHooks): Conversations {
  let s = idle();
  const show = (b: DialogueBeat): void => {
    s.beat = b;
    if (b.kind === "end") {
      c.close();
      return;
    }
    if (b.kind === "line") {
      s.speaker = b.speaker ?? s.npc;
      s.lines++;
      // a line held behind a wait is announced when the wait ends
      if (!s.waiting.length) announce();
    } else {
      s.speaker = null;
      if (!s.waiting.length) hooks.onChoices?.(b.choices.map((x) => x.id));
    }
  };
  const announce = (): void => {
    const b = s.beat;
    if (b?.kind === "line") {
      const text = hooks.text(b.id);
      hooks.onLine?.({ speaker: s.speaker ?? "", stringId: b.id, text, seconds: lineSeconds(text) });
    } else if (b?.kind === "choices") hooks.onChoices?.(b.choices.map((x) => x.id));
  };
  const c: Conversations = {
    runner,
    open(npc, script, knot) {
      if (s.npc) throw new Error(`dialogue: already talking with ${s.npc}`);
      s = { ...idle(), npc, script };
      runner.start(script, knot);
      hooks.onOpen?.(npc, script);
      show(runner.next());
    },
    advance() {
      if (!s.npc || s.waiting.length || s.beat?.kind !== "line") return;
      show(runner.next());
    },
    choose(n) {
      const b = s.beat;
      if (!s.npc || s.waiting.length || b?.kind !== "choices") return;
      const ch = b.choices[n];
      if (!ch) throw new Error(`dialogue: no choice ${n} (have ${b.choices.length})`);
      runner.choose(ch.index);
      show(runner.next());
    },
    wait(tag) {
      if (!s.waiting.includes(tag)) s.waiting.push(tag);
    },
    release(tag) {
      const was = s.waiting.length;
      s.waiting = s.waiting.filter((t) => t !== tag);
      if (was && !s.waiting.length && s.npc) announce();
    },
    close() {
      const npc = s.npc;
      s = idle();
      if (npc) hooks.onClose?.(npc);
    },
    state: () => structuredClone(s),
    active: () => s.npc !== null,
    snapshot: () => ({ conversation: structuredClone(s), runner: runner.snapshot() }),
    restore(data) {
      const d = data as { conversation: ConversationState; runner: unknown };
      if (!d || typeof d.conversation !== "object") throw new Error("dialogue: not a conversation snapshot");
      runner.restore(d.runner);
      s = structuredClone(d.conversation);
    },
  };
  return c;
}
